import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { ChevronRight, Truck, Globe, MessageCircle } from 'lucide-react';

const fmtTime = ts => {
  if (!ts || ts === 'now') return 'الآن';
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

const translate = async (text, from, to) => {
  try {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${from}&tl=${to}&dt=t&q=${encodeURIComponent(text)}`;
    const r = await fetch(url);
    const d = await r.json();
    return d?.[0]?.map(x => x?.[0]).filter(Boolean).join('') || '';
  } catch { return ''; }
};

export default function OrderChatPage() {
  const { id: orderId } = useParams();
  const { apiCall }     = useCustomerAuth();
  const navigate        = useNavigate();

  const [msgs,         setMsgs]         = useState([]);
  const [input,        setInput]         = useState('');
  const [sending,      setSending]       = useState(false);
  const [error,        setError]         = useState('');
  const [translations, setTranslations]  = useState({});
  const [autoTrans,    setAutoTrans]     = useState(false);
  const [lastId,       setLastId]        = useState(null);
  const bottomRef = useRef(null);
  const pollRef   = useRef(null);

  const load = async () => {
    try {
      const params = lastId ? `?since_id=${lastId}` : '';
      const d = await apiCall(`/customer/chat/${orderId}${params}`);
      if (d.messages?.length) {
        setMsgs(prev => {
          const ids = new Set(prev.map(m => m.id));
          const newOnes = d.messages.filter(m => !ids.has(m.id));
          return [...prev, ...newOnes];
        });
        const last = d.messages[d.messages.length - 1];
        if (last) setLastId(last.id);
      }
    } catch {}
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 3000);
    return () => clearInterval(pollRef.current);
  }, [orderId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

  useEffect(() => {
    if (!autoTrans) return;
    msgs.forEach(async m => {
      if (m.role === 'driver' && !translations[m.id]) {
        const t = await translate(m.content, m.lang === 'en' ? 'en' : 'ar', 'ar');
        if (t) setTranslations(p => ({ ...p, [m.id]: t }));
      }
    });
  }, [msgs, autoTrans]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true); setError('');
    try {
      const res = await apiCall(`/customer/chat/${orderId}`, {
        method: 'POST',
        body: JSON.stringify({ content: text }),
      });
      setMsgs(p => [...p, { ...res, created_at: new Date().toISOString() }]);
      setInput('');
    } catch (e) { setError(e.message || 'فشل الإرسال'); }
    finally     { setSending(false); }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const C = {
    surface: 'var(--wt-surface)',
    border:  'var(--wt-border)',
    text:    'var(--wt-text-primary)',
    muted:   'var(--wt-text-muted)',
  };

  return (
    <div dir="rtl" style={{ display:'flex', flexDirection:'column', height:'calc(100dvh - 64px)', fontFamily:"'Cairo',sans-serif" }}>
      <style>{`
        @keyframes oc-spin { to{transform:rotate(360deg)} }
        @keyframes oc-in   { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        .oc-input:focus { border-color:rgba(10,186,181,0.7) !important; box-shadow:0 0 0 3px rgba(10,186,181,0.12) !important; }
        .oc-input::placeholder { opacity:0.4; }
        .oc-bubble-me   { border-radius:1rem 1rem 0.25rem 1rem; }
        .oc-bubble-them { border-radius:1rem 1rem 1rem 0.25rem; }
      `}</style>

      {/* ══ HEADER ══ */}
      <div style={{
        padding:'0.75rem 1rem', display:'flex', alignItems:'center', gap:'0.75rem',
        background:'var(--wt-surface)', borderBottom:'1px solid var(--wt-border)',
        flexShrink:0,
      }}>
        <button onClick={() => navigate(`/customer/orders/${orderId}`)} style={{
          width:36, height:36, borderRadius:'0.75rem', border:'1px solid var(--wt-border)',
          background:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
          color: C.muted, flexShrink:0,
        }}>
          <ChevronRight size={16}/>
        </button>

        <div style={{
          width:40, height:40, borderRadius:'0.875rem', flexShrink:0,
          background:'linear-gradient(135deg,rgba(10,186,181,0.25),rgba(8,145,178,0.15))',
          border:'1.5px solid rgba(10,186,181,0.35)',
          display:'flex', alignItems:'center', justifyContent:'center',
        }}><Truck size={20} color="#0ABAB5" strokeWidth={1.8}/></div>

        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:'0.875rem', fontWeight:800, color: C.text }}>السائق</div>
          <div style={{ fontSize:'0.68rem', color:'#22c55e', display:'flex', alignItems:'center', gap:'0.3rem', marginTop:'0.1rem' }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:'#22c55e', display:'inline-block' }}/>
            محادثة مباشرة
          </div>
        </div>

        <button onClick={() => setAutoTrans(p => !p)} style={{
          fontSize:'0.7rem', fontWeight:700, padding:'0.3rem 0.7rem', borderRadius:999,
          border:`1px solid ${autoTrans ? 'rgba(10,186,181,0.5)' : 'var(--wt-border)'}`,
          background: autoTrans ? 'rgba(10,186,181,0.12)' : 'transparent',
          color: autoTrans ? '#0ABAB5' : C.muted,
          cursor:'pointer', fontFamily:"'Cairo',sans-serif", transition:'all 0.15s',
        }}>
          <Globe size={12}/> {autoTrans ? 'ترجمة ✓' : 'ترجمة'}
        </button>
      </div>

      {/* ══ MESSAGES ══ */}
      <div style={{
        flex:1, overflowY:'auto', padding:'1rem', display:'flex', flexDirection:'column', gap:'0.625rem',
        background:'var(--wt-bg, var(--wt-surface))',
      }}>
        {msgs.length === 0 && (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', flex:1, gap:'0.75rem', opacity:0.5 }}>
            <div style={{ width:72, height:72, borderRadius:'1.25rem', background:'rgba(10,186,181,0.1)', border:'1px solid rgba(10,186,181,0.25)', display:'flex', alignItems:'center', justifyContent:'center' }}><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/><path d="M8 10h8M8 14h5" strokeOpacity="0.6"/></svg></div>
            <p style={{ fontSize:'0.82rem', color: C.muted, margin:0 }}>ابدأ المحادثة مع السائق</p>
          </div>
        )}

        {msgs.map(m => {
          const isMe = m.role === 'customer';
          return (
            <div key={m.id} style={{ display:'flex', flexDirection:'column', alignItems: isMe ? 'flex-end' : 'flex-start', animation:'oc-in 0.2s ease' }}>
              {!isMe && (
                <div style={{ fontSize:'0.62rem', color: C.muted, marginBottom:'0.2rem', marginRight:'0.25rem' }}>السائق</div>
              )}
              <div className={isMe ? 'oc-bubble-me' : 'oc-bubble-them'} style={{
                maxWidth:'78%', padding:'0.625rem 0.875rem',
                background: isMe
                  ? 'linear-gradient(135deg,#0ABAB5,#0891b2)'
                  : 'var(--wt-surface)',
                border: isMe ? 'none' : '1px solid var(--wt-border)',
                color: isMe ? '#fff' : C.text,
                fontSize:'0.875rem', lineHeight:1.55,
                boxShadow: isMe ? '0 4px 12px rgba(10,186,181,0.25)' : '0 1px 4px rgba(0,0,0,0.08)',
              }}>
                {m.content}

                {translations[m.id] && (
                  <div style={{ fontSize:'0.75rem', marginTop:'0.4rem', paddingTop:'0.4rem', borderTop:`1px solid ${isMe ? 'rgba(255,255,255,0.2)' : 'var(--wt-border)'}`, opacity:0.85, fontStyle:'italic' }}>
                    <Globe size={11} style={{display:'inline',marginLeft:3}}/> {translations[m.id]}
                  </div>
                )}

                {!isMe && !translations[m.id] && (
                  <button onClick={async () => {
                    const t = await translate(m.content, m.lang === 'en' ? 'en' : 'ar', 'ar');
                    if (t) setTranslations(p => ({ ...p, [m.id]: t }));
                  }} style={{ display:'block', fontSize:'0.65rem', color: C.muted, background:'none', border:'none', cursor:'pointer', marginTop:'0.25rem', padding:0, fontFamily:"'Cairo',sans-serif" }}>
                    <Globe size={10} style={{display:'inline',marginLeft:2}}/> ترجم
                  </button>
                )}

                <div style={{ fontSize:'0.6rem', opacity:0.55, marginTop:'0.25rem', textAlign:'left' }}>
                  {fmtTime(m.created_at)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* ══ ERROR ══ */}
      {error && (
        <div style={{ padding:'0.4rem 1rem', fontSize:'0.78rem', color:'#f43f5e', background:'rgba(244,63,94,0.08)', borderTop:'1px solid rgba(244,63,94,0.15)', flexShrink:0 }}>
          {error}
        </div>
      )}

      {/* ══ INPUT ══ */}
      <div style={{
        padding:'0.625rem 0.875rem', display:'flex', gap:'0.5rem', alignItems:'flex-end',
        background:'var(--wt-surface)', borderTop:'1px solid var(--wt-border)', flexShrink:0,
      }}>
        <textarea
          className="oc-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="اكتب رسالة للسائق..."
          rows={1}
          style={{
            flex:1, border:'1.5px solid var(--wt-border)', borderRadius:'0.875rem',
            padding:'0.625rem 0.875rem', fontSize:'0.875rem', resize:'none',
            outline:'none', fontFamily:"'Cairo',sans-serif",
            minHeight:44, maxHeight:120,
            background:'rgba(255,255,255,0.04)', color: C.text,
            transition:'border-color 0.15s, box-shadow 0.15s',
          }}
        />
        <button onClick={send} disabled={sending || !input.trim()} style={{
          width:44, height:44, borderRadius:'0.875rem', border:'none', cursor:'pointer',
          background:'linear-gradient(135deg,#0ABAB5,#0891b2)',
          display:'flex', alignItems:'center', justifyContent:'center',
          opacity: sending || !input.trim() ? 0.45 : 1,
          flexShrink:0, boxShadow:'0 4px 12px rgba(10,186,181,0.3)',
          transition:'opacity 0.15s',
        }}>
          {sending
            ? <span style={{ width:18, height:18, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'oc-spin 0.7s linear infinite', display:'block' }}/>
            : <svg viewBox="0 0 24 24" fill="currentColor" style={{ width:18, height:18 }}><path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z"/></svg>
          }
        </button>
      </div>
    </div>
  );
}
